import { InputWithLabel } from '@adotel/components'
import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  CircularProgress,
  Modal,
  Paper,
  Slide,
  Typography
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import WorkIcon from '@material-ui/icons/Work'
import { Skeleton } from '@material-ui/lab'
import { matchSorter } from 'match-sorter'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { PROVIDERS_SUBMIT_LIST_QUERY } from '../../graphql/accommodationProviderSubmit'
import { PMS_TRIGGER_MUTATION } from '../../graphql/pmsTrigger'
import { getDirection, getTranslate, lang } from '../../localization'
import { ProviderSubmit } from '../../types/accommodationProviderSubmit'
import useStyles from './index.styles'

export interface ImportDataMpdalProps {
  /**
   * To get provider list
   */
  propertyCode: string
  /**
   * For control modal be open or not
   */
  isOpen: boolean
  /**
   * A function for closing modal
   */
  onClose: () => void
}

export const ImportDataMpdal: React.FC<ImportDataMpdalProps> = ({
  propertyCode,
  isOpen,
  onClose
}) => {
  const classes = useStyles()
  const [searchInputValue, setSearchInputValue] = useState('')
  const [selectedProvider, setSelectedProvider] = useState<{
    value: string
    label: string
  }>({
    value: '',
    label: ''
  })
  const [filteredProvider, setFilteredProvider] = useState<ProviderSubmit[]>([])

  const { data: providerListData, loading: loadingForProviderSubmitList } =
    useQuery<{
      providersSubmitList: ProviderSubmit[]
    }>(PROVIDERS_SUBMIT_LIST_QUERY, {
      notifyOnNetworkStatusChange: true,
      variables: {
        propertyCode: propertyCode
      },
      onError: (error) => {
        toast.error(error.message)
      }
    })

  const [importData, { loading: loadingForImportData }] = useMutation(
    PMS_TRIGGER_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (d) => {
        toast.success(d.PMSTrigger.message)
      },
      onError: (error) => {
        toast.error(error.message)
      }
    }
  )

  useEffect(() => {
    const fullProviderList = providerListData?.providersSubmitList || []
    const newFullProviderList = matchSorter(
      fullProviderList,
      searchInputValue,
      {
        keys: [
          'providerCode',
          'providerName',
          'providerNameTranslatedList.value'
        ]
      }
    )

    setFilteredProvider(newFullProviderList)
  }, [providerListData, searchInputValue])

  return (
    <Modal open={isOpen} onClose={onClose} dir={getDirection()}>
      <Slide direction="down" in={isOpen}>
        <Paper className={classes.deletePaper}>
          <div className={classes.header}>
            <div>{<WorkIcon />}</div>

            <Typography variant="h3" className={classes.space}>
              {getTranslate('Import Data for Which Provider?')}
            </Typography>
            <Typography variant="h5" className={classes.space}>
              {getTranslate('choose Provider')}
            </Typography>
            <div className={classes.inputWithLable}>
              <InputWithLabel
                value={searchInputValue}
                onChange={(e) => {
                  setSearchInputValue(e.currentTarget.value)
                }}
                placeholder={getTranslate('Search by name or code')}
                inputSize={'small'}
                noHelperText
                fullWidth
                startIcon={<SearchIcon />}
              />
            </div>
          </div>
          <div className={classes.itemsBox}>
            <div className={classes.listView}>
              {loadingForProviderSubmitList ? (
                <div className={classes.skeletonContainer}>
                  {Array(5)
                    .fill('')
                    .map((_, index) => (
                      <div key={index}>
                        <Skeleton
                          className={classes.skeleton}
                          variant="rect"
                          width="100%">
                          <div />
                        </Skeleton>
                      </div>
                    ))}
                </div>
              ) : (
                filteredProvider.map((provider, index) => (
                  <div
                    key={index}
                    className={[
                      classes.listRow,
                      selectedProvider.value === provider.providerCode &&
                        classes.selectedListRow
                    ].join(' ')}
                    onClick={() => {
                      setSelectedProvider({
                        value: provider.providerCode,
                        label:
                          lang === 'en'
                            ? provider.providerName || ''
                            : provider.providerNameTranslatedList![0].value ||
                              provider.providerName ||
                              ''
                      })
                    }}>
                    <Typography>
                      {lang === 'en'
                        ? provider.providerName
                        : provider.providerNameTranslatedList![0].value ||
                          provider.providerName}
                    </Typography>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className={classes.buttonsContainer}>
            <Button
              variant="text"
              color="primary"
              onClick={() => {
                onClose()
              }}>
              {getTranslate('Close')}
            </Button>
            <Button
              disabled={loadingForImportData || !selectedProvider.value}
              variant="contained"
              className={classes.deleteButton}
              onClick={() => {
                importData({
                  variables: {
                    propertyCode: propertyCode,
                    providerCode: selectedProvider.value
                  }
                })
              }}
              color="primary">
              {getTranslate('Import')}
              {loadingForImportData && (
                <CircularProgress
                  className={classes.circularProgress}
                  size={16}
                />
              )}
            </Button>
          </div>
        </Paper>
      </Slide>
    </Modal>
  )
}
